import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import api from '../services/api';
import { Facility } from '../types';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';

interface FacilityContextType {
  selectedFacilityId: string | null;
  selectedFacility: Facility | null;
  availableFacilities: Facility[];
  setSelectedFacility: (facilityId: string | null) => void;
  refreshFacilities: () => Promise<void>;
  isLoading: boolean;
}

const FacilityContext = createContext<FacilityContextType | undefined>(undefined);

export { FacilityContext };

export const useFacilityContext = () => {
  const ctx = useContext(FacilityContext);
  if (!ctx) {
    throw new Error('useFacilityContext must be used within FacilityProvider');
  }
  return ctx;
};

interface FacilityProviderProps {
  children: ReactNode;
}

const FACILITY_STORAGE_KEY = 'facilityContext';

export const FacilityProvider: React.FC<FacilityProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { showError } = useToast();
  const [availableFacilities, setAvailableFacilities] = useState<Facility[]>([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState<string | null>(null);
  const [selectedFacility, setSelectedFacility] = useState<Facility | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const applySelection = useCallback(
    (facilityId: string | null, facilities: Facility[]) => {
      if (!facilityId) {
        setSelectedFacilityId(null);
        setSelectedFacility(null);
        localStorage.removeItem(FACILITY_STORAGE_KEY);
        return;
      }

      const found = facilities.find((f) => f.id === facilityId) || null;
      if (found) {
        setSelectedFacilityId(facilityId);
        setSelectedFacility(found);
        localStorage.setItem(FACILITY_STORAGE_KEY, facilityId);
      } else {
        // Clear if invalid
        setSelectedFacilityId(null);
        setSelectedFacility(null);
        localStorage.removeItem(FACILITY_STORAGE_KEY);
      }
    },
    []
  );

  const refreshFacilities = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      // Use facility access endpoint to get facilities the user can access
      const res = await api.get(`/facility-access/user/${user.id}/facilities`);
      if (res.data.success) {
        const facilities = res.data.data.facilities || [];
        setAvailableFacilities(facilities);

        if (facilities.length === 0) {
          applySelection(null, facilities);
          return;
        }

        // For owner-like roles with multiple facilities:
        // 1. Try to load last selected facility from localStorage
        // 2. If not found (first time), randomly select one
        // 3. For other roles or single facility, use stored or first facility
        let selectedFacilityId: string | null = null;

        if ((user.role === 'supervisor' || user.role === 'facility_owner' || user.role === 'caregiver') && facilities.length > 1) {
          // Check for last selected facility
          const stored = localStorage.getItem(FACILITY_STORAGE_KEY);
          if (stored && facilities.some((f: Facility) => f.id === stored)) {
            // Use stored facility if it's still available
            selectedFacilityId = stored;
          } else {
            // First time or stored facility no longer available - randomly select one
            const randomIndex = Math.floor(Math.random() * facilities.length);
            selectedFacilityId = facilities[randomIndex].id;
          }
        } else {
          // For other roles or single facility, use stored or first facility
          const stored = localStorage.getItem(FACILITY_STORAGE_KEY);
          selectedFacilityId = stored || (facilities.length > 0 ? facilities[0].id : null);
        }

        applySelection(selectedFacilityId, facilities);
      }
    } catch (err: any) {
      console.error('Failed to load facilities:', err);
      showError('Failed to load facilities');
    } finally {
      setIsLoading(false);
    }
  }, [applySelection, showError, user]);

  useEffect(() => {
    if (user) {
      refreshFacilities();
    } else {
      setAvailableFacilities([]);
      setSelectedFacilityId(null);
      setSelectedFacility(null);
    }
  }, [user, refreshFacilities]);

  const setSelectedFacilityHandler = (facilityId: string | null) => {
    applySelection(facilityId, availableFacilities);
  };

  return (
    <FacilityContext.Provider
      value={{
        selectedFacilityId,
        selectedFacility,
        availableFacilities,
        setSelectedFacility: setSelectedFacilityHandler,
        refreshFacilities,
        isLoading
      }}
    >
      {children}
    </FacilityContext.Provider>
  );
};




